import { API_CONFIG } from "../../../config/api";
import { apiClientBff } from "../../../services/apiBff";
import { tokenService } from "../../../services/auth/tokenService";

// Types for Flowise integration (migrated from agentChatMessage)
export interface FlowiseConfig {
  id: string;
  name: string;
  description: string;
  endpoint: string;
  apiKey: string;
  isActive: boolean;
  priority: number;
  organizationType?: string[];
  keywords?: string[];
}

export interface FlowiseRequest {
  question: string;
  sessionId?: string;
  chatId?: string;
  organizationId?: string;
  organizationType?: string;
  context?: Record<string, any>;
}

export interface FlowiseResponse {
  text: string;
  sourceDocuments?: any[];
  chatId?: string;
  sessionId?: string;
  agentReasoning?: any[];
  usedTools?: any[];
  fileAnnotations?: any[];
}

export interface FlowSelectionResult {
  selectedFlow: FlowiseConfig;
  confidence: number;
  reason: string;
}

// Global bearer token used for all Flowise requests (managed by BFF now)
const GLOBAL_FLOWISE_BEARER_TOKEN = ""; 

// Flow configurations are now loaded dynamically from BFF
const FLOWISE_FLOWS: FlowiseConfig[] = [];

// Types matching the ChatSessions API schema
export interface ChatSession {
  session_id: string;
  username: string;
  organization_id?: string | null;
  organization_type?: string | null;
  flow_name: string;
  title?: string | null;
  created_at: string;
  updated_at?: string;
  last_used?: string;
  status: string;
  created_by?: string;
  updated_by?: string;
}

export interface ChatMessage {
  message_id: string;
  session_id: string;
  sender_type: "user" | "assistant" | "system";
  sender_id: string;
  content: string;
  timestamp: string;
  message_type: "text" | "image" | "file" | "system";
  metadata?: object | null;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  // Legacy fields for compatibility
  id?: string;
  sender?: "user" | "assistant";
  status?: "sending" | "sent" | "error";
}

export interface CreateSessionRequest {
  organizationId?: string;
  organizationType?: string;
  title?: string;
  flowName?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Simplified service for managing chat sessions and messages
 * Replaces: AgentChatMessageService, ConversationTypeService
 */
export class ChatSessionService {
  private apiBaseUrl: string;
  private username: string;
  private flows: FlowiseConfig[];
  private defaultFlow: FlowiseConfig;

  constructor(username: string, apiBaseUrl: string = API_CONFIG.baseURL) {
    this.username = username;
    this.apiBaseUrl = apiBaseUrl;
    this.flows = [];
    this.defaultFlow = {
      id: "master",
      name: "Master Flow",
      description: "General purpose agricultural assistant",
      endpoint: "",
      apiKey: "",
      isActive: true,
      priority: 1,
      organizationType: ["other", "general"],
    };
    
    // Load flows asynchronously
    this.loadFlows();
  }

  /**
   * Load flows configuration from BFF
   */
  async loadFlows(): Promise<void> {
    try {
      const response = await apiClientBff.get<{ success: boolean; flows: FlowiseConfig[] }>("/flowise/flows");
      if (response.success && Array.isArray(response.flows)) {
        this.flows = response.flows;
        this.defaultFlow = this.flows.find((flow) => flow.id === "master") || this.flows[0] || this.defaultFlow;
      }
    } catch (error) {
      console.warn("ChatSessionService: Failed to load flows from BFF", error);
    }
  }

  /**
   * Create a new chat session
   */
  async createSession(
    request: CreateSessionRequest
  ): Promise<ApiResponse<ChatSession>> {
    try {
      const sessionData: Omit<ChatSession, "session_id"> = {
        username: this.username,
        organization_id: request.organizationId || null,
        organization_type: request.organizationType || null,
        flow_name:
          request.flowName || this.getDefaultFlowName(request.organizationType),
        title: request.title || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_used: new Date().toISOString(),
        status: "active",
        created_by: this.username,
        updated_by: this.username,
      };

      const sessionWithId = {
        ...sessionData,
        session_id: `session_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      const result = await apiClientBff.post<{ data: ChatSession }>(
        "/chat-sessions/insert",
        {
          data: sessionWithId,
        }
      );

      return {
        success: true,
        data: sessionWithId,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get user's chat sessions
   */
  async getUserSessions(
    organizationId?: string
  ): Promise<ApiResponse<ChatSession[]>> {
    try {
      // Buscar sessões do usuário com filtro de status e ordenação por last_used
      // Observação: evitar campos "created_at"/"updated_at" na query para não disparar validação de palavras proibidas
      const query = organizationId
        ? `FOR doc IN @@collection 
            FILTER doc.username == @username 
              AND doc.status == @status 
              AND doc.organization_id == @organizationId 
            SORT doc.last_used DESC 
            LIMIT @limit 
            RETURN doc`
        : `FOR doc IN @@collection 
            FILTER doc.username == @username 
              AND doc.status == @status 
            SORT doc.last_used DESC 
            LIMIT @limit 
            RETURN doc`;

      const bindVars: any = {
        "@collection": "ChatSessions",
        username: this.username,
        status: "active",
        limit: 100,
      };

      if (organizationId) {
        bindVars.organizationId = organizationId;
      }

      const result = await apiClientBff.post<any>(
        "/chat-sessions/search",
        {
          query,
          bind_vars: bindVars,
        }
      );

      // Aceitar tanto respostas em array direto quanto envolvidas em { data }
      const rawSessions: any[] = Array.isArray(result)
        ? (result as any[])
        : ((result as any)?.data || []);

      // Garantir que session_id exista, usando fallback para _key/id
      const sessions: ChatSession[] = rawSessions.map((doc: any) => ({
        ...doc,
        session_id: doc.session_id || doc._key || doc.id,
      }));

      return {
        success: true,
        data: sessions,
      };
    } catch (error) {
      console.error("ChatSessionService: Error in getUserSessions:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Update session metadata
   */
  async updateSession(
    sessionId: string,
    updates: Partial<ChatSession>
  ): Promise<ApiResponse<ChatSession>> {
    try {
      const result = await apiClientBff.post<{ data: ChatSession[] }>(
        "/chat-sessions/update",
        {
          query:
            "FOR doc IN @@collection FILTER doc.session_id == @sessionId UPDATE doc WITH @updates IN @@collection RETURN NEW",
          bind_vars: {
            "@collection": "ChatSessions",
            sessionId,
            updates: {
              ...updates,
              updated_at: new Date().toISOString(),
              updated_by: this.username,
            },
          },
        }
      );
      return {
        success: true,
        data: (result as any).data?.[0],
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Delete a session and all its associated messages
   */
  async deleteSession(sessionId: string): Promise<ApiResponse<void>> {
    try {
      // First delete all messages associated with the session (best-effort)
      try {
        const messagesResult = await apiClientBff.post<any>(
          "/chat-messages/delete",
          {
            query:
              "FOR doc IN @@collection FILTER doc.session_id == @sessionId REMOVE doc IN @@collection RETURN OLD",
            bind_vars: {
              "@collection": "ChatMessages",
              sessionId,
            },
          }
        );
        // Accept both array result or object-shaped success
        const deletionOk = Array.isArray(messagesResult) || messagesResult?.success === true;
        if (!deletionOk) {
          console.warn(
            "ChatSessionService: message deletion returned unexpected shape",
            messagesResult
          );
        }
      } catch (msgErr) {
        // Do not block session deletion if messages deletion fails
        console.warn("ChatSessionService: error deleting messages (continuing):", msgErr);
      }

      // Then delete the session itself
      await apiClientBff.post<any>(
        "/chat-sessions/delete",
        {
          query:
            "FOR doc IN @@collection FILTER doc.session_id == @sessionId REMOVE doc IN @@collection RETURN OLD",
          bind_vars: {
            "@collection": "ChatSessions",
            sessionId,
          },
        }
      );

      // Both operations successful

      return {
        success: true,
        data: undefined,
      };
    } catch (error) {
      console.error("ChatSessionService: Error in deleteSession:", error);

      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Unknown error occurred during session deletion",
      };
    }
  }

  /**
   * Update session last used timestamp
   */
  async touchSession(sessionId: string): Promise<void> {
    await this.updateSession(sessionId, {
      last_used: new Date().toISOString(),
    });
  }

  /**
   * Get messages for a specific session
   */
  async getMessages(sessionId: string): Promise<ApiResponse<ChatMessage[]>> {
    try {
      const requestPayload = {
        query:
          "FOR doc IN @@collection FILTER doc.session_id == @sessionId SORT doc.timestamp ASC RETURN doc",
        bind_vars: {
          "@collection": "ChatMessages",
          sessionId,
        },
      };

      const result = await apiClientBff.post<any>(
        "/chat-messages/search",
        requestPayload
      );

      // Aceitar tanto respostas em array direto quanto envolvidas em { data }
      const rawMessages: any[] = Array.isArray(result)
        ? (result as any[])
        : ((result as any)?.data || []);

      // Transformar e normalizar campos para compatibilidade com UI
      const messages: ChatMessage[] = rawMessages.map((msg: any) => {
        const contentStr = typeof msg.content === 'string' ? msg.content : '';
        let fallbackContent = contentStr;
        if (!fallbackContent || !fallbackContent.trim()) {
          const md = msg.metadata || {};
          const tasksArr = Array.isArray(md.tasks) ? md.tasks : [];
          if (tasksArr.length > 0) {
            fallbackContent = tasksArr.join('\n- ');
          } else if (typeof md.question === 'string' && md.question.trim()) {
            fallbackContent = md.question;
          } else if (typeof md.resumo === 'string' && md.resumo.trim()) {
            fallbackContent = md.resumo;
          } else if (typeof md.url === 'string' && md.url.trim()) {
            fallbackContent = md.url;
          }
        }
        return {
          ...msg,
          content: fallbackContent,
          message_id: msg.message_id || msg._key || msg.id,
          id: msg.message_id || msg._key || msg.id,
          sender: msg.sender_type === "user" ? "user" : "assistant",
          status: "sent",
        } as ChatMessage;
      });

      return {
        success: true,
        data: messages,
      };
    } catch (error) {
      console.error("Error fetching messages:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Save a message to the database
   */
  async saveMessage(message: ChatMessage): Promise<ApiResponse<ChatMessage>> {
    try {
      // Constrói somente campos aceitos pelo schema do ChatMessages
      const baseMessageData: any = {
        message_id:
          message.message_id ||
          message.id ||
          `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        session_id: message.session_id,
        sender_type:
          message.sender_type ||
          (message.sender === "user" ? "user" : "assistant"),
        sender_id: message.sender_id || this.username,
        content: message.content,
        timestamp: message.timestamp || new Date().toISOString(),
        message_type: message.message_type || "text",
      };

      // Incluir metadata apenas se for um objeto válido
      if (message.metadata && typeof message.metadata === "object") {
        baseMessageData.metadata = message.metadata;
      }

      return {
        success: true,
        data: baseMessageData,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  /**
   * Get default flow name based on organization type
   */
  private getDefaultFlowName(organizationType?: string): string {
    const flowMap: Record<string, string> = {
      farm: "pastos",
      dairy: "lifecycle-born",
      slaughterhouse: "master",
      cooperative: "master",
    };

    return flowMap[organizationType || ""] || "master";
  }

  /**
   * Select the most appropriate flow based on request context (migrated from agentChatMessage)
   */
  private selectFlow(request: FlowiseRequest): FlowSelectionResult {
    let bestFlow = this.defaultFlow;
    let maxScore = 0;
    let reason = "Default master flow";

    // Only consider active flows
    const activeFlows = this.flows.filter((flow) => flow.isActive);

    for (const flow of activeFlows) {
      let score = 0;
      let currentReason = "";

      // Organization type matching (highest priority)
      if (
        request.organizationType &&
        flow.organizationType?.includes(request.organizationType)
      ) {
        score += 100;
        currentReason = `Organization type match: ${request.organizationType}`;
      }

      // Keyword matching in question
      if (flow.keywords && request.question) {
        const questionLower = request.question.toLowerCase();
        const matchedKeywords = flow.keywords.filter((keyword) =>
          questionLower.includes(keyword.toLowerCase())
        );

        if (matchedKeywords.length > 0) {
          score += matchedKeywords.length * 10;
          currentReason += currentReason
            ? ` + Keywords: ${matchedKeywords.join(", ")}`
            : `Keywords: ${matchedKeywords.join(", ")}`;
        }
      }

      // Priority bonus (lower priority number = higher bonus)
      score += 10 - flow.priority;

      if (score > maxScore) {
        maxScore = score;
        bestFlow = flow;
        reason = currentReason || `Priority: ${flow.priority}`;
      }
    }

    return {
      selectedFlow: bestFlow,
      confidence: Math.min(maxScore / 100, 1), // Normalize to 0-1
      reason,
    };
  }

  /**
   * Execute query against selected Flowise flow (migrated from agentChatMessage)
   */
  private async executeFlowQuery(
    flow: FlowiseConfig,
    data: any
  ): Promise<FlowiseResponse> {
    try {
      // Use BFF proxy instead of direct fetch
      const response = await apiClientBff.post<{ success: boolean; data: FlowiseResponse }>(
        "/flowise/message",
        {
          flowId: flow.id,
          payload: data,
        }
      );

      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error("Flowise query failed via BFF");
    } catch (error) {
      console.error(`Error executing flow ${flow.id}:`, error);
      throw error;
    }
  }

  /**
   * Get conversation history for a specific session (enhanced version from agentChatMessage)
   */
  async getConversationHistory(
    sessionId: string,
    organizationId?: string
  ): Promise<{
    messages: ChatMessage[];
    success: boolean;
    error?: string;
  }> {
    try {
      const result = await this.getMessages(sessionId);

      if (result.success && result.data) {
        return {
          messages: result.data,
          success: true,
        };
      } else {
        return {
          messages: [],
          success: false,
          error: result.error || "Failed to retrieve conversation history",
        };
      }
    } catch (error) {
      return {
        messages: [],
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Process message with Flowise AI and handle database persistence (migrated from agentChatMessage)
   */
  async processMessage(request: FlowiseRequest): Promise<{
    success: boolean;
    response?: FlowiseResponse;
    flowUsed?: FlowiseConfig;
    databaseStored: boolean;
    conversationHistory?: ChatMessage[];
    error?: string;
  }> {
    const timestamp = new Date().toISOString();

    try {
      // Select appropriate flow
      const selectionResult = this.selectFlow(request);

      // Prepare data for Flowise
      const flowData = {
        question: request.question,
        chatId: request.chatId,
        sessionId: request.sessionId,
        ...request.context,
      };

      // Execute query
      const response = await this.executeFlowQuery(
        selectionResult.selectedFlow,
        flowData
      );

      // Get updated conversation history
      const historyResult = await this.getConversationHistory(
        request.sessionId || "",
        request.organizationId
      );

      return {
        success: true,
        response,
        flowUsed: selectionResult.selectedFlow,
        databaseStored: false,
        conversationHistory: historyResult.success
          ? historyResult.messages
          : [],
      };
    } catch (error) {
      console.error("Error in processMessage:", error);

      // Store error message in database
      const errorMessage: ChatMessage = {
        message_id: `msg_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        session_id: request.sessionId || "",
        sender_type: "system",
        sender_id: "system",
        content: `Error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: new Date().toISOString(),
        message_type: "text",
        metadata: {
          isError: true,
          organizationId: request.organizationId,
          organizationType: request.organizationType,
        },
      };

      await this.saveMessage(errorMessage);

      // Try fallback to master flow if available and not already used
      if (this.defaultFlow.isActive) {
        try {
          const fallbackData = {
            question: request.question,
            chatId: request.chatId,
            sessionId: request.sessionId,
          };

          const fallbackResponse = await this.executeFlowQuery(
            this.defaultFlow,
            fallbackData
          );

          // Store fallback response in database
          const fallbackMessage: ChatMessage = {
            message_id: `msg_${Date.now()}_${Math.random()
              .toString(36)
              .substr(2, 9)}`,
            session_id: request.sessionId || "",
            sender_type: "assistant",
            sender_id: "ai-assistant",
            content: fallbackResponse.text,
            timestamp: new Date().toISOString(),
            message_type: "text",
            metadata: {
              organizationId: request.organizationId,
              organizationType: request.organizationType,
              flowName: this.defaultFlow.name + " (fallback)",
              isFallback: true,
            },
          };

          return {
            success: true,
            response: fallbackResponse,
            flowUsed: this.defaultFlow,
            databaseStored: false,
            conversationHistory: [],
          };
        } catch (fallbackError) {
          console.error("Fallback also failed:", fallbackError);
        }
      }

      return {
        success: false,
        databaseStored: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }

  /**
   * Get available flows (migrated from agentChatMessage)
   */
  getAvailableFlows(): FlowiseConfig[] {
    return this.flows.filter((flow) => flow.isActive);
  }

  /**
   * Test connection to a specific flow (migrated from agentChatMessage)
   */
  async testFlow(flowId: string): Promise<boolean> {
    const flow = this.flows.find((f) => f.id === flowId);
    if (!flow) {
      throw new Error(`Flow ${flowId} not found`);
    }

    try {
      const testData = {
        question: "Test connection",
      };

      await this.executeFlowQuery(flow, testData);
      return true;
    } catch (error) {
      console.error(`Flow ${flowId} test failed:`, error);
      return false;
    }
  }
}

// Try to get the username from cookies (with legacy migration inside tokenService)
const userFromCookie = tokenService.getAuthUserCookie();
const initialUsername = userFromCookie?.username || userFromCookie?.full_name || userFromCookie?.email || "anonymous";

// Export singleton instance
export const chatSessionService = new ChatSessionService(initialUsername);

/**
 * Update the username in the ChatSessionService
 * This should be called when the user logs in or out
 */
export function updateChatSessionUsername(username: string): void {
  const oldUsername = chatSessionService["username"];
  chatSessionService["username"] = username || "anonymous";
}
